import React, { useEffect, useState, useRef } from 'react';
import BackWithSidebar from '../../../../components/BackWithSidebar';
import GeneralIcon from '../../../../assets';
import SearchBar from '../../../../components/SearchBar';
import categoryService from '../../../../services/categoryService';
import { mapCategoryFromApi, buildCategoryPayload } from '../../../../models/categoryModel';

function Modal({ open, onClose, children, title }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100">
            ←
          </button>
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        </div>
        {children}
      </div>
    </div>
  );
}

function CategoryItem({ category, onEdit, onDelete }) {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function loadImage() {
      if (category.imageKey) {
        const url = await categoryService.getCategoryImageUrl(category.imageKey);
        if (mounted) setImageUrl(url);
      }
    }
    loadImage();
    return () => { mounted = false; };
  }, [category.imageKey]);

  return (
    <div className="bg-white border rounded-lg p-4 flex gap-4 items-start">
      {/* Image */}
      <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
        {imageUrl ? (
          <img src={imageUrl} alt={category.name} className="w-full h-full object-cover" />
        ) : (
          <div className="text-gray-300 text-xs">img</div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900">{category.name}</div>
        <div className="text-sm text-gray-600 mt-1">{category.description}</div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-2 rounded border border-purple-300 text-purple-600 hover:bg-purple-50 transition"
          title="Editar"
        >
          <GeneralIcon iconName="editar" className="w-5 h-5" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 rounded border border-red-300 text-red-600 hover:bg-red-50 transition"
          title="Excluir"
        >
          <GeneralIcon iconName="lixeira" className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default function Categorias() {
  const [itemType, setItemType] = useState('Mercadoria');
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState('');

  const imageInputRef = useRef(null);

  useEffect(() => {
    loadCategories();
  }, [itemType]);

  useEffect(() => {
    // Filter categories by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      setFilteredCategories(
        categories.filter(c =>
          c.name.toLowerCase().includes(query) ||
          c.description.toLowerCase().includes(query)
        )
      );
    } else {
      setFilteredCategories(categories);
    }
  }, [searchQuery, categories]);

  async function loadCategories() {
    setLoading(true);
    try {
      const data = await categoryService.fetchCategories(itemType);
      setCategories(data.map(mapCategoryFromApi));
    } catch (err) {
      console.error('loadCategories error:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleAddNew() {
    setEditingCategory(null);
    setName('');
    setDescription('');
    setImageFile(null);
    setImagePreview(null);
    setError('');
    setShowModal(true);
  }

  async function handleEdit(category) {
    setEditingCategory(category);
    setName(category.name);
    setDescription(category.description);
    setImageFile(null);
    setError('');

    // Load existing image
    if (category.imageKey) {
      const url = await categoryService.getCategoryImageUrl(category.imageKey);
      setImagePreview(url);
    } else {
      setImagePreview(null);
    }

    setShowModal(true);
  }

  async function handleDelete(id) {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;

    const result = await categoryService.deleteCategory(id);
    if (result.success) {
      await loadCategories();
    } else {
      alert(result.error || 'Erro ao excluir categoria');
    }
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  async function handleSave() {
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Nome da categoria é obrigatório');
      return;
    }

    const payload = buildCategoryPayload({
      name: name.trim(),
      description: description.trim(),
      itemType,
      imageFile,
    });

    let result;
    if (editingCategory) {
      result = await categoryService.updateCategory(editingCategory.id, payload);
    } else {
      result = await categoryService.createCategory(payload);
    }

    if (result.error) {
      setError(result.error);
      return;
    }

    if (result.success) {
      setShowModal(false);
      await loadCategories();
    }
  }

  return (
    <div className="p-4 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <BackWithSidebar to="/dashboard" className="p-2 rounded-full hover:bg-gray-100" />
        <h2 className="text-lg font-semibold">Categorias</h2>
      </div>

      <div className="bg-white rounded-lg p-4 shadow-sm">
        {/* Item Type Selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de item
          </label>
          <select
            value={itemType}
            onChange={e => setItemType(e.target.value)}
            className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
          >
            <option value="Mercadoria">Mercadoria</option>
          </select>
        </div>

        {/* Search */}
        <SearchBar
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Buscar"
          className="mb-4"
        />

        {/* Results count */}
        {filteredCategories.length > 0 && (
          <div className="text-sm text-gray-600 mb-4">
            Encontramos {filteredCategories.length} resultado{filteredCategories.length !== 1 ? 's' : ''}
          </div>
        )}

        {/* Category List */}
        <div className="space-y-3 mb-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Carregando...</div>
          ) : filteredCategories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <GeneralIcon iconName="vazio" className="w-32 h-32 mb-4" />
              <div className="text-sm">Nenhuma categoria encontrada</div>
            </div>
          ) : (
            filteredCategories.map(category => (
              <CategoryItem
                key={category.id}
                category={category}
                onEdit={() => handleEdit(category)}
                onDelete={() => handleDelete(category.id)}
              />
            ))
          )}
        </div>

        {/* Add Button */}
        <button
          onClick={handleAddNew}
          className="w-full py-3 rounded-lg bg-purple-600 text-white font-medium hover:bg-purple-700 transition flex items-center justify-center gap-2"
        >
          <span className="text-xl">+</span>
          Adicionar uma nova categoria
        </button>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingCategory ? 'Editar categoria' : 'Adicionar categoria'}
      >
        <div className="space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Imagem*
            </label>
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="w-full border-2 border-dashed border-purple-300 rounded-xl py-8 flex flex-col items-center justify-center hover:bg-purple-50 transition"
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-24 h-24 object-cover rounded-lg"
                />
              ) : (
                <>
                  <GeneralIcon iconName="formas" className="w-16 h-16 text-gray-300 mb-2" />
                  <div className="flex items-center gap-2 text-purple-600">
                    <GeneralIcon iconName="camera" className="w-5 h-5" />
                    <span className="text-sm font-medium">Alterar a imagem</span>
                  </div>
                </>
              )}
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome da categoria*
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Bebidas"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição*
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Refrigerantes, sucos, energéticos e vitaminas."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm text-center bg-red-50 py-2 rounded-lg">
              {error}
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            className="w-full py-3 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition"
          >
            {editingCategory ? 'Atualizar' : 'Adicionar'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
